import React, {useEffect, useState} from "react";
import {usePluginContext} from "../../core/plugin/PluginContext";
import {ReportingPlugin} from "../../core/plugin/PluginType";
import {pluginRegistry} from "../../core/plugin/PluginRegistry";
import WidgetComponent from "../../core/components/WidgetComponent";
import {Box, CircularProgress, Container, Grid} from "@mui/material";
import {Widget} from "../dashboard/Dashboard";
import {Report} from "../../api/ReportApi";
import {Button} from "../../components/Button";
import Header from "../../components/Header";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Typography} from "../../components/Typography";
import {useReports} from "../../hooks/useReports";
import {ReportWidget} from "./ReportWidget";
import clsx from "clsx";


interface AnalyticsProps {
}

const Analytics: React.FC<AnalyticsProps> = (props) => {

    const {t} = useTranslation();
    const navigate = useNavigate();

    const [widgets, setWidgets] = useState<Widget[]>([]);
    const [availablePlugins, setAvailablePlugins] = useState<ReportingPlugin[]>([]);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const pluginContext = usePluginContext();

    const {
        reports,
        loading: loadingReports,
        executeReport,
        executing
    } = useReports();

    // Load dashboard configuration and plugins
    useEffect(() => {
        const loadDashboard = async () => {
            try {
                setIsLoading(true);

                // Load available dashboard plugins
                const reportingPlugins = pluginRegistry.getPluginsByType<ReportingPlugin>('reporting');
                setAvailablePlugins(reportingPlugins);
                createDefaultDashboard(reportingPlugins);

            } catch (err) {
                console.error('Error loading dashboard:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadDashboard();
    }, [pluginContext]);

    // Create a default dashboard with available plugins
    const createDefaultDashboard = (plugins: ReportingPlugin[]) => {
        const defaultWidgets: Widget[] = plugins.slice(0, 4).map((plugin, index) => ({
            id: `widget-${index}`,
            pluginId: plugin.id,
            title: plugin.name,
            size: plugin.defaultSize || 'medium',
            preferences: plugin.defaultPreferences || {}
        }));

        setWidgets(defaultWidgets);
    };

    const handleCreateReportClick = () => {
        navigate(`/analytics/create-report`);
    };

    // Handle chat message - this is where you would integrate with your AI service
    const handleChatMessage = async (message: string): Promise<string> => {
        // TODO: Replace this with actual API call to your AI service
        // Example:
        // const response = await fetch('/api/analytics/chat', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ message })
        // });
        // const data = await response.json();
        // return data.response;

        // Simulated response for demonstration
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Simple keyword-based responses for demo purposes
        const lowerMessage = message.toLowerCase();

        if (lowerMessage.includes('tenure') || lowerMessage.includes('average')) {
            return "Based on current employee data, the average tenure across all employees is 3.2 years. The Sales department shows the highest average tenure at 4.1 years, while Engineering has 2.8 years. Would you like me to break this down by department or seniority level?";
        } else if (lowerMessage.includes('turnover') || lowerMessage.includes('attrition')) {
            return "For Q4 2023, the overall turnover rate was 8.5%, which is slightly below the industry average of 10%. The highest turnover was observed in the Customer Support department (12%), while Operations had the lowest at 5%. I can generate a detailed trend analysis if needed.";
        } else if (lowerMessage.includes('headcount') || lowerMessage.includes('department')) {
            return "Current headcount distribution by department:\n\n• Engineering: 145 employees (32%)\n• Sales: 98 employees (22%)\n• Customer Support: 87 employees (19%)\n• Operations: 73 employees (16%)\n• Marketing: 49 employees (11%)\n\nTotal headcount: 452 employees. Would you like to see growth trends or drill down into any specific department?";
        } else if (lowerMessage.includes('diversity') || lowerMessage.includes('gender')) {
            return "Current diversity metrics show a 52/48 gender split across the organization. Leadership positions reflect a 58/42 ratio. The Engineering department has the most balanced distribution at 50/50. I can provide more detailed breakdowns by level, department, or other demographic factors.";
        } else {
            return `I understand you're asking about "${message}". I can help you analyze employee data including:\n\n• Turnover and retention trends\n• Headcount and department distribution\n• Tenure and engagement metrics\n• Diversity and inclusion statistics\n• Compensation analysis\n\nCould you provide more specific details about what you'd like to know?`;
        }
    };

    const handleReportExecution = async (report: Report) => {
        // TODO: Check report.id is not undefined here?
        const executionResult = await executeReport(report.id);
        return executionResult;
    };

    return (
        <React.Fragment>
            <Header
                className={'mt-8'}
                title={t('pages.analytics.title')}
                loading={loadingReports}
            >
                <Button
                    onClick={handleCreateReportClick}
                    variant="contained"
                    color="primary"
                    isDisabled={isLoading}
                >
                    {t('analytics.actions.createNew')}
                </Button>
            </Header>

            {/* TODO Define minWidth in Layout or similar */}
            <Container disableGutters maxWidth={false} style={{minWidth: '600px'}}
                       sx={{borderRadius: 4, ml: 4, mr: 3, mb: 4, pr: 3, flexGrow: 1, width: 'auto'}}>

                {/* Chat Section - Main Feature */}
                {/*<div className="mb-6">*/}
                {/*    <Chat*/}
                {/*        onSendMessage={handleChatMessage}*/}
                {/*        maxHeight="500px"*/}
                {/*        className="w-full"*/}
                {/*    />*/}
                {/*</div>*/}

                {loadingReports ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 10}}>
                        <CircularProgress/>
                    </Box>

                ) : reports.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 10,
                        px: 3,
                    }}
                    >
                        <Typography variant="h6">
                            {t('analytics.empty.title')}
                        </Typography>
                        <Typography variant="body2" className={'mt-2 mb-4'}>
                            {t('analytics.empty.subtitle')}
                        </Typography>
                        <Button
                            onClick={handleCreateReportClick}
                            variant="contained"
                            color="primary"
                        >
                            {t('analytics.actions.createFirst')}
                        </Button>
                    </Box>
                ) : (
                    // <Box sx={{mb: 3}}>
                    //     {reports.map(report => (
                    //         <ReportWidget report={report} loading={executing} onExecute={handleReportExecution} />
                    //     ))}
                    //     {/*<TemplateGrid templates={templates}*/}
                    //     {/*              onEdit={handleEditClick}*/}
                    //     {/*              onDownload={handleTemplateDownload}*/}
                    //     {/*              onDelete={handleDeleteClick}*/}
                    //     {/*              onInstantiate={handleInstantiateClick}*/}
                    //     {/*/>*/}
                    // </Box>

                    <div
                        className={clsx(
                            'grid gap-4 justify-start',
                            // className
                        )}
                        style={{
                            gridTemplateColumns: 'repeat(auto-fill, 520px)',
                        }}
                        role="list"
                        aria-label={t('templates.grid.label')}
                    >
                        {reports.map(report => (
                            <div key={report.id} role="listitem">
                                <ReportWidget
                                    report={report}
                                    loading={executing}
                                    height={380}
                                    width={520}
                                    onExecute={handleReportExecution}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Widgets Section */}
                {widgets.length > 0 && (
                    <div className="mt-8">
                        <div className="mb-4">
                            <Typography variant="h5" color="primary" className="font-semibold">
                                {t('pages.analytics.widgets.title') || 'Analytics Insights'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" className="mt-1">
                                {t('pages.analytics.widgets.subtitle') || 'Key metrics and trends at a glance'}
                            </Typography>
                        </div>

                        <Grid container spacing={2}>
                            {widgets.map((widget, index) => (
                                <Grid item sx={{flex: '0 0 auto', width: 450}} key={widget.id}>
                                    <div className={`dashboard-widget-wrapper size-${widget.size}`}>
                                        <WidgetComponent
                                            widget={widget}
                                            plugin={availablePlugins.find(p => p.id === widget.pluginId)}
                                            onUpdateWidget={() => console.log('Update widget')}
                                            onRemoveWidget={() => console.log('Remove widget')}
                                        />
                                    </div>
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                )}
            </Container>
        </React.Fragment>
    );
};

export default Analytics;